import { Link } from "react-router-dom";
import { Car } from "lucide-react";
import { TH } from "./StageCells";
import { fmtTd, IdleTag } from "./TdFleet";

// Group Overview block: which models' TD cars sit idle across the group (≤ threshold % of the group's all-model TDs/car/day).
export function TdModelDemand({ d, period }) {
  if (!d) return null;
  const idleRows = d.rows.filter((r) => r.idle);
  return (
    <div className="border bg-card rounded-md" data-testid="ov-td-models">
      <div className="px-4 py-3 border-b flex flex-wrap items-center gap-2">
        <h3 className="font-heading text-sm font-semibold inline-flex items-center gap-1.5"><Car size={14} /> I · Per-model fleet demand{period?.is_week ? ` — ${period.week}` : ""} <span className="text-muted-foreground font-normal text-xs">· idle TD cars across the group · model TDs/car/day at or below {d.threshold_pct}% of the group figure{d.threshold != null ? ` (${fmtTd(d.group_per_car_day)} → idle ≤ ${fmtTd(d.threshold)})` : ""}</span></h3>
        <span className="ml-auto text-[10px] text-muted-foreground" data-testid="ov-td-models-coverage">{d.dealers_with_split} dealer{d.dealers_with_split === 1 ? "" : "s"} with a model-wise split · {d.dealers_without_split} without (total only)</span>
      </div>
      {d.rows.length === 0 ? (
        <p className="px-4 py-6 text-xs text-muted-foreground text-center" data-testid="ov-td-models-empty">No dealer has a model-wise TD-car split yet — add one in Admin › Dealers › TD cars to see per-model demand.</p>
      ) : (
        <div className="overflow-auto">
          <table className="w-full text-xs" data-testid="ov-td-models-table">
            <thead className="border-b">
              <tr>{["Model", "TD cars", "Dealers", "Test drives", "TDs / car / day", "vs group", "Idle at"].map((h, i) => <th key={h} className={`${TH} ${i === 0 || i === 6 ? "text-left" : "text-right"}`}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.model} className="border-b last:border-0 hover:bg-muted/50" data-testid={`ov-td-model-${r.model.replace(/\s+/g, "_")}`}>
                  <td className="px-2 py-1.5 font-medium whitespace-nowrap">{r.model}{r.idle && <IdleTag testId={`ov-td-model-idle-${r.model.replace(/\s+/g, "_")}`} />}</td>
                  <td className="px-2 py-1.5 text-right num">{r.td_cars}</td>
                  <td className="px-2 py-1.5 text-right num text-muted-foreground">{r.dealers}</td>
                  <td className="px-2 py-1.5 text-right num">{r.test_drives}</td>
                  <td className="px-2 py-1.5 text-right num font-semibold" title={`${r.test_drives} test drives ÷ ${r.car_days} car-days`}>{fmtTd(r.per_car_day)}</td>
                  <td className="px-2 py-1.5 text-right num text-muted-foreground">{r.vs_group == null ? "—" : `${Math.round(r.vs_group)}%`}</td>
                  <td className="px-2 py-1.5">
                    {r.idle_at.length === 0 ? <span className="text-muted-foreground">—</span> : (
                      <span className="flex flex-wrap gap-1">
                        {r.idle_at.map((x) => (
                          <Link key={x.code} to={`/dpc/dealer/${x.code}`} className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] hover:bg-secondary transition-colors duration-200" title={`${x.test_drives} test drives on ${x.td_cars} ${r.model} TD car${x.td_cars === 1 ? "" : "s"}`} data-testid={`ov-td-model-idle-at-${r.model.replace(/\s+/g, "_")}-${x.code}`}>
                            <b>{x.code}</b> · {x.td_cars} car{x.td_cars === 1 ? "" : "s"} · <span className="num">{fmtTd(x.per_car_day)}</span>
                          </Link>
                        ))}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {idleRows.length > 0 && (
            <p className="px-4 py-2 text-[10px] text-muted-foreground border-t" data-testid="ov-td-models-hint">
              Rebalance: {idleRows.map((r) => `${r.model} (${r.idle_at.reduce((s, x) => s + x.td_cars, 0)} idle car${r.idle_at.reduce((s, x) => s + x.td_cars, 0) === 1 ? "" : "s"})`).join(", ")} — move idle cars to dealers running above the group figure or re-deploy them to another model.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
